import { Controller } from "@hotwired/stimulus"
import { Primer } from '@primer-io/checkout-web'
import { get, post } from "@rails/request.js"

// for testing
// https://primer.io/docs/payments/testing

export default class extends Controller {
  static targets = ["checkoutContainer", "actions", "clientToken", "pleaseWait",
    "paymentError", "paymentSuccess", "featuresAndBenefits", "countdown", "logosContainer", "errorDetails"]
  static values = {
    countdown: { type: Number, default: 5 },
    autoOpen: { type: Boolean, default: false },
    url: { type: String, default: "" },
  }

  connect() {
    if (this.autoOpenValue) {
      this.openPaymentsForm()
    }
  }

  disconnect() {
    this.stopTimers()
  }

  // https://primer.io/docs/payments/universal-checkout/drop-in/get-started/web
  openPaymentsForm() {
    this.paymentErrorTarget.classList.add("hidden")
    this.checkoutContainerTarget.classList.remove("hidden")
    if (this.hasActionsTarget) {
      this.actionsTarget.classList.add("hidden")
    }
    if (this.hasFeaturesAndBenefitsTarget) {
      this.featuresAndBenefitsTarget.classList.add("hidden")
    }

    const clientToken = this.clientTokenTarget.textContent
    const _this = this

    Primer.showUniversalCheckout(clientToken, {
      // Specify the selector of the container element
      container: this.checkoutContainerTarget,

      /**
        * When the checkout flow has been completed, you'll receive
        * the successful payment via `onCheckoutComplete`.
        * Implement this callback to redirect the user to an order confirmation page.
        */
      onCheckoutComplete({ payment }) {
        console.log('Checkout Complete!', payment)
        // Sections:
        // - features and benefits
        // - actions
        // Available only on subscription purchase pages.
        if (_this.hasFeaturesAndBenefitsTarget) {
          _this.featuresAndBenefitsTarget.classList.add("hidden")
        }
        if (_this.hasActionsTarget) {
          _this.actionsTarget.classList.add("hidden")
        }

        _this.checkoutContainerTarget.classList.add("hidden")
        if (_this.hasLogosContainerTarget) {
          _this.logosContainerTarget.remove()
        }
        _this.paymentErrorTarget.classList.add("hidden")
        _this.pleaseWaitTarget.classList.remove("hidden")
      },
      /**
        * When the checkout flow has been fail, you'll receive
        * the error via `onCheckoutFail`
        */
      async onCheckoutFail(error, { payment }, handler) {
        if (!handler) {
          return
        }
        // Show a default error message
        handler.showErrorMessage()

        console.log('Checkout Fail!', error, payment)
        if (_this.hasLogosContainerTarget) {
          _this.logosContainerTarget.remove()
        }
        _this.errorDetailsTarget.textContent = error?.description || error?.toString() || "Failed to process your payment."
        _this.errorDetailsTarget.classList.add("hidden")

        // report error to server, so we can investigate it, send it to Sentry, etc.
        await post("/report-error", {
          body: {
            error_type: "primer_checkout_fail",
            code: error?.code,
            details: error?.details,
            error: error?.toString(),
            diagnostics_id: error?.diagnosticsId,
          },
        })

        _this.pleaseWaitTarget.classList.add("hidden")
        _this.paymentErrorTarget.classList.remove("hidden")
      },

      onTokenizeSuccess(paymentMethodTokenData, handler) {
        // TODO: check if we can use it
        console.log('Tokenize Success!', paymentMethodTokenData)
      },

      submitButton: {
        onVisible() {
          if (_this.hasLogosContainerTarget) {
            setTimeout(() => {
              // do not show logos when we have previously saved payment method visible on the page
              if (!document.querySelector('.PrimerCheckout__savedPaymentMethodDetail')) {
                _this.logosContainerTarget.classList.remove("hidden")
              }
            }, 300);
          }
        },
      },

      // vaultOnSuccess(paymentMethodTokenData, handler) {
      //   console.log('Vault Success!', paymentMethodTokenData)
      // },

      // vaultOnFail(error, handler) {
      //   console.log('Vault Fail!', error)
      // },
    })
  }

  paymentSuccess() {
    if (this.urlValue) {
      // after user added payment details, we can show the original payment form (for example, subscribe form)
      get(this.urlValue, {
        responseKind: "turbo-stream",
      })
      this.urlValue = ""
    } else {
      this.pleaseWaitTarget.classList.add("hidden")
      this.paymentErrorTarget.classList.add("hidden")
      this.checkoutContainerTarget.classList.add("hidden")
      if (this.hasLogosContainerTarget) {
        this.logosContainerTarget.remove()
      }
      this.paymentSuccessTarget.classList.remove("hidden")
      this.countdownTarget.textContent = this.countdownValue

      this.countdownInterval = setInterval(() => {
        this.countdownTarget.textContent = parseInt(this.countdownTarget.textContent) - 1
      }, 1000)

      this.countdownTimeout = setTimeout(() => {
        this.stopTimers()
        this.reloadPage()
      }, this.countdownValue * 1000)
    }
  }

  reloadPage() {
    window.location.reload()
  }

  stopTimers() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
    if (this.countdownTimeout) {
      clearTimeout(this.countdownTimeout)
    }
  }
}
